import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Star, Zap, Crown, Shield, Users, Briefcase, Award, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { useAuth } from '../contexts/AuthContext';
export function UpgradeMembershipPage() {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const currentPlan = user?.jenisKeanggotaan || 'INDIVIDU_PELAJAR';
  const plans = [{
    id: 'INDIVIDU_UMUM',
    name: 'Anggota Individu (Umum)',
    price: 'Rp 500.000',
    period: '/tahun',
    icon: Briefcase,
    color: 'text-atios-primary',
    bgColor: 'bg-atios-light',
    badge: 'POPULER',
    features: ['Semua fitur pelajar', 'Bursa lowongan kerja premium', 'Akses acara networking eksklusif', 'Konsultasi karir profesional', 'Sertifikasi profesional', 'Diskon 30% untuk semua acara', 'Akses materi pembelajaran premium', 'Prioritas pendaftaran event'],
    benefits: [{
      icon: Star,
      text: 'Networking dengan 10.000+ profesional'
    }, {
      icon: Award,
      text: 'Sertifikat keanggotaan digital'
    }, {
      icon: TrendingUp,
      text: 'Career development support'
    }]
  }, {
    id: 'ORGANISASI_PENDIDIKAN',
    name: 'Anggota Organisasi',
    price: 'Rp 5.000.000',
    period: '/tahun',
    icon: Users,
    color: 'text-success',
    bgColor: 'bg-success-light',
    badge: 'TERBAIK UNTUK TIM',
    features: ['Kuota 24 anggota organisasi', 'Branding korporat di platform', 'Alat rekrutmen terintegrasi', 'Dashboard analitik lengkap', 'Pendaftaran anggota massal', 'Akses API untuk integrasi', 'Dedicated account manager', 'Laporan bulanan'],
    benefits: [{
      icon: Users,
      text: '24 slot anggota'
    }, {
      icon: Shield,
      text: 'Branding & visibility'
    }, {
      icon: Zap,
      text: 'Priority support'
    }]
  }, {
    id: 'BADAN_USAHA_KECIL',
    name: 'Anggota Badan Usaha',
    price: 'Rp 3.000.000',
    period: '/tahun',
    icon: Crown,
    color: 'text-warning',
    bgColor: 'bg-warning-light',
    badge: 'ENTERPRISE',
    features: ['Semua fitur organisasi', 'Posting lowongan kerja unlimited', 'Akses database talent', 'Branding premium', 'Event sponsorship opportunities', 'Custom integration', 'Dedicated support 24/7', 'Quarterly business review'],
    benefits: [{
      icon: Briefcase,
      text: 'Unlimited job postings'
    }, {
      icon: Users,
      text: 'Access to talent pool'
    }, {
      icon: Crown,
      text: 'Premium branding'
    }]
  }];
  const handleUpgrade = () => {
    if (selectedPlan) {
      navigate('/payment', {
        state: {
          upgradeFrom: currentPlan,
          upgradeTo: selectedPlan
        }
      });
    }
  };
  return <div className="min-h-screen bg-white" data-id="element-5237">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5238">
        {/* Header */}
        <div className="mb-8" data-id="element-5239">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-id="element-5240">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-5241" />
            Kembali
          </Button>
          <h1 className="text-3xl font-semibold text-neutral-900 mb-2" data-id="element-5242">
            Tingkatkan Keanggotaan
          </h1>
          <p className="text-neutral-600" data-id="element-5243">
            Pilih paket yang sesuai dengan kebutuhan Anda dan dapatkan lebih
            banyak manfaat
          </p>
        </div>

        {/* Current Plan */}
        <Card className="mb-8 bg-gradient-to-r from-atios-light to-white border-atios-primary" data-id="element-5244">
          <CardContent className="p-6" data-id="element-5245">
            <div className="flex items-center justify-between" data-id="element-5246">
              <div data-id="element-5247">
                <p className="text-sm text-neutral-600 mb-1" data-id="element-5248">Paket Saat Ini</p>
                <h3 className="text-xl font-semibold text-neutral-900" data-id="element-5249">
                  {currentPlan === 'INDIVIDU_PELAJAR' && 'Anggota Individu (Pelajar)'}
                  {currentPlan === 'INDIVIDU_UMUM' && 'Anggota Individu (Umum)'}
                  {currentPlan === 'ORGANISASI_PENDIDIKAN' && 'Anggota Organisasi'}
                </h3>
              </div>
              <Badge variant="success" data-id="element-5250">AKTIF</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Plans */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8" data-id="element-5251">
          {plans.map(plan => <Card key={plan.id} className={`relative cursor-pointer transition-all ${selectedPlan === plan.id ? 'border-2 border-atios-primary shadow-lg' : 'hover:border-neutral-300'}`} onClick={() => setSelectedPlan(plan.id)} data-id="element-5252">
              {plan.badge && <div className="absolute -top-3 left-1/2 -translate-x-1/2" data-id="element-5253">
                  <Badge variant="primary" className="text-xs px-3 py-1 shadow-sm" data-id="element-5254">
                    {plan.badge}
                  </Badge>
                </div>}

              <CardHeader data-id="element-5255">
                <div className={`h-14 w-14 rounded-xl ${plan.bgColor} flex items-center justify-center mb-4`} data-id="element-5256">
                  <plan.icon className={`w-7 h-7 ${plan.color}`} data-id="element-5257" />
                </div>
                <CardTitle className="text-xl mb-2" data-id="element-5258">{plan.name}</CardTitle>
                <div className="mb-4" data-id="element-5259">
                  <span className="text-3xl font-bold text-neutral-900" data-id="element-5260">
                    {plan.price}
                  </span>
                  <span className="text-neutral-500 text-sm ml-1" data-id="element-5261">
                    {plan.period}
                  </span>
                </div>
              </CardHeader>

              <CardContent className="space-y-6" data-id="element-5262">
                {/* Features */}
                <div data-id="element-5263">
                  <p className="text-sm font-semibold text-neutral-900 mb-3" data-id="element-5264">
                    Fitur Utama:
                  </p>
                  <ul className="space-y-2" data-id="element-5265">
                    {plan.features.map((feature, i) => <li key={i} className="flex items-start text-sm text-neutral-700" data-id="element-5266">
                        <Check className="w-4 h-4 text-success mr-2 mt-0.5 flex-shrink-0" data-id="element-5267" />
                        {feature}
                      </li>)}
                  </ul>
                </div>

                {/* Benefits */}
                <div className="pt-4 border-t border-neutral-200" data-id="element-5268">
                  <p className="text-sm font-semibold text-neutral-900 mb-3" data-id="element-5269">
                    Manfaat Tambahan:
                  </p>
                  <div className="space-y-2" data-id="element-5270">
                    {plan.benefits.map((benefit, i) => <div key={i} className="flex items-center gap-2 text-sm text-neutral-700" data-id="element-5271">
                        <benefit.icon className={`w-4 h-4 ${plan.color}`} data-id="element-5272" />
                        {benefit.text}
                      </div>)}
                  </div>
                </div>

                <Button fullWidth variant={selectedPlan === plan.id ? 'primary' : 'secondary'} onClick={e => {
              e.stopPropagation();
              setSelectedPlan(plan.id);
            }} data-id="element-5273">
                  {selectedPlan === plan.id ? <>
                      <Check className="w-4 h-4 mr-2" data-id="element-5274" />
                      Terpilih
                    </> : 'Pilih Paket'}
                </Button>
              </CardContent>
            </Card>)}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4" data-id="element-5275">
          <Button variant="secondary" onClick={() => navigate(-1)} className="flex-1" data-id="element-5276">
            Batal
          </Button>
          <Button onClick={handleUpgrade} disabled={!selectedPlan} className="flex-1" data-id="element-5277">
            Lanjutkan ke Pembayaran
          </Button>
        </div>
      </div>
    </div>;
}